import { AnimalParents } from 'api/query/animal/animal-parents';
import { CatalogueItem } from 'api/query/catalogue/catalogue-item';
import { CatalogueSortBy } from 'api/query/catalogue/catalogue-sort-by.enum';
import { CatalogueSortDir } from 'api/query/catalogue/catalogue-sort-dir.enum';
import { SaleType } from 'api/query/catalogue/sale-type.enum';
import { SearchAnimal } from 'api/query/search';
import { Trait } from 'api/query/shared';
import { Column } from 'components/table/column';
import { AppConstants } from 'constants/app-constants.enum';
import moment from 'moment';
import { ASBVsTab } from 'pages/search/asbvs/asbvs-tab.enum';
import { asbvsDefaultState } from 'pages/search/asbvs/asbvs-utils';
import { errorsInitialState } from 'pages/search/filters';
import { basicDefaultState } from 'pages/search/filters/all/basic';
import { pedigreeDefaultState } from 'pages/search/filters/all/pedigree/pedigree-utils';
import { progenyDefaultState } from 'pages/search/filters/all/progeny/progeny-utils';
import { getSortOptions } from 'pages/search/results/results-utils';
import { SortDir } from 'pages/search/results/sort-dir.enum';
import { AnimalId } from 'shared/animal-id.constant';
import { Catalogues } from './catalogue-constants.enum';

export interface Parents {
  sire: string,
  dam: string,
}

export enum NoCataloguesMessage {
  NoSalesCataloguesTitle = 'No sale catalogues currently listed',
  NoSemenCataloguesTitle = 'No semen catalogues currently listed',
}

export enum NoCataloguesMessageDescription {
  CreateNewSaleCatalogue = 'Create a new sales catalogue to list animals for sale',
  CreateNewSemenCatalogue = 'Create a new semen catalogue to list semen for sale',
}

export const catalogueDateFormat = 'ddd D MMMM YYYY';

interface HeaderType {
  id: CatalogueSortBy,
  label: string,
}

const individualSaleHeader: HeaderType[] = [
  {
    id: CatalogueSortBy.Lot,
    label: 'lot',
  },
  {
    id: CatalogueSortBy.Price,
    label: 'price',
  },
  {
    id: CatalogueSortBy.Title,
    label: AnimalId,
  },
];

const individualSemenHeader: HeaderType[] = [
  {
    id: CatalogueSortBy.Price,
    label: 'price',
  },
  {
    id: CatalogueSortBy.Dose,
    label: 'doses',
  },
  {
    id: CatalogueSortBy.Title,
    label: AnimalId,
  },
];

const salesHeaders: HeaderType[] = [
  {
    id: CatalogueSortBy.Title,
    label: 'sale title',
  },
  {
    id: CatalogueSortBy.SaleDate,
    label: 'sale date',
  },
  {
    id: CatalogueSortBy.Listed,
    label: 'listed animals',
  },
  {
    id: CatalogueSortBy.Flock,
    label: 'flock',
  },
  {
    id: CatalogueSortBy.Location,
    label: 'location',
  },
  {
    id: CatalogueSortBy.SaleType,
    label: 'sale type',
  },
];

const semenHeaders: HeaderType[] = [
  {
    id: CatalogueSortBy.Title,
    label: 'catalogue title',
  },
  {
    id: CatalogueSortBy.Listed,
    label: 'listed animals',
  },
  {
    id: CatalogueSortBy.Flock,
    label: 'flock',
  },
  {
    id: CatalogueSortBy.Location,
    label: 'location',
  },
];

const mySalesHeaders: HeaderType[] = [
  {
    id: CatalogueSortBy.Title,
    label: 'sale title',
  },
  {
    id: CatalogueSortBy.PublishedEndDate,
    label: 'Published',
  },
  {
    id: CatalogueSortBy.Listed,
    label: 'listed animals',
  },
  {
    id: CatalogueSortBy.SaleDate,
    label: 'sale date',
  },
  {
    id: CatalogueSortBy.SaleType,
    label: 'sale type',
  },
];

const mySemenHeaders: HeaderType[] = [
  {
    id: CatalogueSortBy.Title,
    label: 'catalogue title',
  },
  {
    id: CatalogueSortBy.Listed,
    label: 'listed animals',
  },
];

export const getTableHeaders = (catalogueType: Catalogues, sortBy: any, sortDir: CatalogueSortDir | SortDir, asbvs?: Trait[]): Column[] => {
  let labels;

  switch (catalogueType) {
    case Catalogues.Sales:
      labels = salesHeaders;
      break;
    case Catalogues.Semen:
      labels = semenHeaders;
      break;
    case Catalogues.IndividualSale:
      labels = individualSaleHeader;
      break;
    case Catalogues.MySalesCatalogues:
      labels = mySalesHeaders;
      break;
    case Catalogues.MySemenCatalogues:
      labels = mySemenHeaders;
      break;
    case Catalogues.IndividualSemen:
      labels = individualSemenHeader;
      break;
    default:
      labels = salesHeaders;
      break;
  }

  const cols: Column[] = [];
  const fakeStickyCol: Column = {
    id: '',
    label: '',
    isSorted: false,
    sortDir: CatalogueSortDir.asc,
    align: 'center',
    isSticky: true,
  };

  const actionCol: Column = {
    id: 'actions',
    /** My catalogues are the only page where tables are designed without label for actions, bring back condition
     * when actions will be implemented for catalogues.
     */
    // label: catalogueType === Catalogues.MySemenCatalogues || catalogueType === Catalogues.MySalesCatalogues ? '' : 'actions',
    label: '',
    enableSort: false,
    isSorted: false,
    sortDir: undefined,
    align: 'center',
    isSticky: true,
  };

  if (catalogueType === Catalogues.Sales || catalogueType === Catalogues.MySemenCatalogues || catalogueType === Catalogues.MySalesCatalogues) { cols.push(fakeStickyCol); }

  const sortDirection = sortDir === SortDir.BestToWorst ? 'desc' : 'asc';
  const individualCatalogue = catalogueType === Catalogues.IndividualSale || catalogueType === Catalogues.IndividualSemen;

  labels.forEach((header: HeaderType, index: number) => {
    const col: Column = {
      id: `${header.id}`,
      label: header.label,
      enableSort: true,
      // Don't use strict equality ('==='), it needs to compare number against string (1 and '1')
      isSorted: individualCatalogue ? (sortBy == header.label) : (sortBy == header.id),
      sortDir: individualCatalogue ? (sortDirection || CatalogueSortDir.asc) : sortDir,
      align: 'left',
      hasInfo: false,
      isSticky: !!((header.label === 'lot' || header.label === 'price' || header.label === 'doses' || header.label === AnimalId)),
    };
    // Ensure catalogue "lot" column has the correct sort
    if (header.label === 'lot') {
      col.sortDir = sortBy === 'lot' ? sortDirection : 'asc';
    }
    // Mirrors sorting behaviour for animal id from data view
    if (header.label === AnimalId) {
      col.sortDir = sortBy === AnimalId ? sortDirection : undefined;
    }
    cols.push(col);
  });

  if (individualCatalogue && asbvs) {
    const dataColumns: Column[] = asbvs.map((a) => ({
      id: a.id,
      label: a.name,
      enableSort: true,
      isSorted: sortBy === a.name,
      sortDir: sortBy === a.name ? sortDirection : 'asc',
      align: 'center',
      hasInfo: true,
      isSticky: false,
    }));
    cols.push(...dataColumns, actionCol);
  }

  if (catalogueType === Catalogues.MySemenCatalogues || catalogueType === Catalogues.MySalesCatalogues) { cols.push(actionCol); }

  if (catalogueType === Catalogues.Sales) { cols.push(fakeStickyCol); }

  return cols;
};

const salesTypes = [
  {
    id: SaleType.AllSemenCatalogue,
    value: 'All semen catalogue',
  },
  {
    id: SaleType.MultiVendorAuction,
    value: 'Multi vendor auction',
  },
  {
    id: SaleType.MultiVendorPrivateTreatySale,
    value: 'Multi-vendor private treaty sale',
  },
  {
    id: SaleType.PrivateTreatySale,
    value: 'Private treaty sale',
  },
  {
    id: SaleType.SingleVendorAuction,
    value: 'Single vendor auction',
  },
];

/**
 * Displays sale type title.
 * @param saleType
 */
export const displaySaleType = (saleType: SaleType) => salesTypes.map((sale: any) => (sale.id === saleType ? sale.value : undefined));

/**
 * Displays one date if start and end sale dates are the same.
 * @param start sale start date
 * @param end sale end date
 */
export const formatDate = (start: string, end?: string) => {
  let dateString;
  if (!end || start === end) {
    dateString = start;
  } else {
    dateString = `<span>${start}</span> - </br>${end}`;
  }

  return dateString;
};

/**
 * Changes display date format example: Mon 1 January 2020.
 * @param start sale start date
 * @param end sale end date
 * @param initialFormat initial format of dat ethat will be changed
 * @param newFormat new format of date
 */
export const changeDateFormat = (start: string, end?: string, initialFormat: string = AppConstants.DateFormat, newFormat: string = catalogueDateFormat) => {
  let value;
  const newStart = moment(start, initialFormat).format(newFormat);

  if (end) {
    const newEnd = moment(end, initialFormat).format(newFormat);
    value = formatDate(newStart, newEnd);
  } else {
    value = formatDate(newStart);
  }

  return value;
};

/**
 * Returns array of numbers from string.
 * @param number phone number
 */
export const formatNumber = (number: string): string[] => number.split(',');

// filters initial state to pass object
export const filtersInitialState = (individual: any) => ({
  basic: basicDefaultState,
  individual,
  progeny: progenyDefaultState,
  pedigree: pedigreeDefaultState,
  asbv: asbvsDefaultState,
  asbvsTab: ASBVsTab.Values,
  expandedFilter: '',
  errors: errorsInitialState,
});

/**
 * Gets all animal ids that exisit in catalogue.
 * @param animals
 */
export const getAnimalIdsFromCatalogue = (animals: CatalogueItem[]) => {
  const ids: string[] = [];
  animals.forEach((animal: CatalogueItem) => {
    ids.push(animal.animalId);
  });

  return ids.toString();
};

/**
 * Displays animal lot number.
 * @param animals
 * @param animalId
 */
export const getAnimalLot = (animals: CatalogueItem[], animalId: string) => {
  let lot;
  animals.forEach((animal) => {
    if (animal.animalId === animalId) {
      lot = animal.lotNo;
    }
  });
  return lot;
};

/**
 * Displays animal sex
 * @param animals
 * @param animalId
 */
export const getAnimalSex = (animals: AnimalParents[], animalId: string) => {
  const animal = animals.find((a) => a.animalId === animalId);
  return animal?.sex;
};

/**
 * Displays animal price.
 * @param animals
 * @param animalId
 */
export const getAnimalPrice = (animals: CatalogueItem[], animalId: string) => {
  let price;
  animals.forEach((animal) => {
    if (animal.animalId === animalId) {
      price = animal.price ? `$${animal.price}` : '';
    }
  });

  return price;
};

/**
 * Displays animal's semen doses.
 * @param animals
 * @param animalId
 */
export const getDose = (animals: CatalogueItem[], animalId: string) => {
  let doses;
  animals.forEach((animal) => {
    if (animal.animalId === animalId) {
      doses = animal.doses ? `${animal.doses}` : '';
    }
  });

  return doses;
};

/**
 * Displays animal's parents.
 * @param animals
 */
export const getAnimalParentsObject = (animals: AnimalParents[], animalId: string) => {
  let parents: Parents = { sire: '', dam: '' };
  animals.forEach((animal) => {
    if (animal.animalId === animalId) {
      parents = { sire: animal.sireStudId, dam: animal.damStudId };
    }
  });

  return parents;
};

/**
 * Gets animal's display id.
 * @param animals
 */
export const getAnimalDisplayId = (animals: CatalogueItem[], animalId: string) => {
  let displayId: string = '';
  animals.forEach((animal) => {
    if (animal.animalId === animalId) {
      displayId = animal.displayAnimalId;
    }
  });

  return displayId;
};

/**
 * Removes price column from table headers.
 * @param cols
 */
export const removePriceCol = (cols: Column[]) => {
  const newCols: Column[] = cols;
  cols.forEach((col: Column, index: number) => {
    if (col.label === 'price') {
      newCols.splice(index, 1);
    }
  });

  return newCols;
};

/**
 * Returns the standard sort options with `lot` included.
 * @param result
 */
export const getCatalogueSortOptions = (result: SearchAnimal) => [{ id: 'lot', value: 'Lot' }, ...getSortOptions(result)];
